import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useState } from "react";
import type { ItsOneViewResponse, ItsOneViewSectionKey, SectionAccess } from "@/types/its-one-view";
import { SECTION_LABELS, SECTION_ORDER, countForSection } from "../section-config";

type SectionSidebarProps = {
  active: ItsOneViewSectionKey;
  onChange: (key: ItsOneViewSectionKey) => void;
  sectionAccess: Record<ItsOneViewSectionKey, SectionAccess>;
  sections: ItsOneViewResponse["sections"];
};

function SidebarList({
  active,
  onChange,
  sectionAccess,
  sections,
  onItemClick,
}: SectionSidebarProps & { onItemClick?: () => void }) {
  return (
    <List dense disablePadding>
      {SECTION_ORDER.map((key) => {
        const access = sectionAccess[key];
        const count = access === "granted" ? countForSection(key, sections) : 0;
        return (
          <ListItemButton
            key={key}
            selected={active === key}
            onClick={() => {
              onChange(key);
              onItemClick?.();
            }}
            disabled={access === "denied"}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {SECTION_LABELS[key]}
                  {access === "denied" ? (
                    <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                  ) : count > 0 ? (
                    <Typography component="span" variant="caption" color="primary">
                      ({count})
                    </Typography>
                  ) : null}
                </Box>
              }
            />
          </ListItemButton>
        );
      })}
    </List>
  );
}

export default function SectionSidebar(props: SectionSidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        <Button
          startIcon={<MenuIcon />}
          variant="outlined"
          size="small"
          onClick={() => setDrawerOpen(true)}
          sx={{ mb: 2 }}
        >
          Sections
        </Button>
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 260, p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Jump to section
            </Typography>
            <SidebarList {...props} onItemClick={() => setDrawerOpen(false)} />
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Box
      sx={{
        width: 220,
        flexShrink: 0,
        pr: 2,
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
        Sections
      </Typography>
      <SidebarList {...props} />
    </Box>
  );
}
